// Unique title/description/canonical for every non-service static route.
// Service pages (/services/*) and blog posts set their own via SEOHead.

import { CLINIC_NAME, DOCTOR_NAME, CLINIC_TIMINGS } from "@/lib/constants";

const SITE = "https://muthaadvanceddentalcare.in";

export interface PageSeo {
  title: string;
  description: string;
  canonical: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

const homeFaqs = [
  {
    q: "What are your clinic timings?",
    a: `We are open Mon-Sat: ${CLINIC_TIMINGS.weekdays}, Sunday: ${CLINIC_TIMINGS.sunday}. Closed on ${CLINIC_TIMINGS.closed}.`,
  },
  { q: "Do you accept dental insurance?", a: "Yes, we accept most major dental insurance plans. Please contact us to verify your specific plan." },
  { q: "Is there parking available?", a: "Yes, convenient parking is available near our clinic in Kalyan." },
  { q: "Do you offer EMI or payment plans?", a: "Yes, we offer flexible EMI options for major treatments. Ask our team for details." },
  { q: "How do I book an appointment?", a: "You can book via WhatsApp, call us directly, or use the online booking form on our website." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: homeFaqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: CLINIC_NAME,
  url: `${SITE}/`,
  inLanguage: "en-IN",
  publisher: { "@type": "Organization", name: CLINIC_NAME, url: `${SITE}/` },
};

/** BreadcrumbList where EVERY ListItem carries an `item` URL — Google Search
 *  Console reports `Missing field "item"` as a critical error otherwise. */
export const breadcrumbJsonLd = (trail: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    ...trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 2,
      name: t.name,
      item: `${SITE}${t.path}`,
    })),
  ],
});

/** Label shown in the breadcrumb trail for each indexable static route. */
const BREADCRUMB_LABELS: Record<string, string> = {
  "/about": "About",
  "/associate-doctors": "Our Dentists",
  "/smile-gallery": "Smile Gallery",
  "/testimonials": "Patient Reviews",
  "/write-review": "Write a Review",
  "/blogs": "Blog",
  "/blog": "Blog",
  "/book-appointment": "Book Appointment",
  "/contact": "Contact",
  "/privacy-policy": "Privacy Policy",
  "/terms-conditions": "Terms & Conditions",
};


export const PAGE_SEO: Record<string, PageSeo> = {
  "/": {
    title: "Best Dentist in Kalyan | Mutha's Advanced Dental Care",
    description:
      "Mutha's Advanced Dental Care, Kalyan — implants, root canal, braces, aligners and smile design by Dr. Khushboo Mutha. Book a consultation on WhatsApp today.",
    canonical: "/",
    jsonLd: [websiteJsonLd, faqJsonLd],
  },
  "/about": {
    title: "About Dr. Khushboo Mutha | Dental Clinic in Kalyan",
    description:
      "Meet Dr. Khushboo Mutha and the team at Mutha's Advanced Dental Care, Kalyan — qualifications, clinic technology, sterilisation standards and patient-first care.",
    canonical: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `About ${DOCTOR_NAME}`,
        url: `${SITE}/about`,
        about: {
          "@type": "Person",
          name: DOCTOR_NAME,
          jobTitle: "Dental Surgeon — Implantology & Prosthodontics",
          worksFor: { "@type": "Dentist", name: CLINIC_NAME, url: `${SITE}/` },
          knowsAbout: ["Dental Implants", "Root Canal Treatment", "Invisalign", "Smile Design", "Prosthodontics"],
          url: `${SITE}/about`,
        },
      },
    ],
  },
  "/associate-doctors": {
    title: "Our Dentists & Specialists in Kalyan | Mutha's Advanced Dental Care",
    description:
      "Meet the orthodontist, endodontist, periodontist and implant specialists at Mutha's Advanced Dental Care in Kalyan, with their consultation days.",
    canonical: "/associate-doctors",
  },
  "/smile-gallery": {
    title: "Smile Gallery — Before & After Results | Dentist in Kalyan",
    description:
      "Real before and after photos from patients treated at Mutha's Advanced Dental Care, Kalyan — implants, veneers, aligners, whitening and full smile makeovers.",
    canonical: "/smile-gallery",
  },
  "/testimonials": {
    title: "Patient Reviews & Testimonials | Dentist in Kalyan",
    description:
      "Read verified patient reviews for Mutha's Advanced Dental Care, Kalyan — rated 4.9 by 500+ patients for implants, root canal, braces and aligners.",
    canonical: "/testimonials",
  },
  "/write-review": {
    title: "Write a Review | Mutha's Advanced Dental Care Kalyan",
    description:
      "Share your experience at Mutha's Advanced Dental Care, Kalyan. Answer a few quick questions and we'll help you draft your Google review.",
    canonical: "/write-review",
  },
  "/blogs": {
    title: "Dental Blog — Tips & Treatment Guides | Dentist in Kalyan",
    description:
      "Dental care guides from Mutha's Advanced Dental Care, Kalyan — implants, braces vs aligners, root canal, whitening costs and everyday oral health tips.",
    canonical: "/blogs",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Dental Blog — Mutha's Advanced Dental Care, Kalyan",
        url: `${SITE}/blogs`,
        isPartOf: { "@type": "WebSite", name: CLINIC_NAME, url: `${SITE}/` },
      },
    ],
  },
  "/blog": {
    title: "Dental Blog — Tips & Treatment Guides | Dentist in Kalyan",
    description:
      "Dental care guides from Mutha's Advanced Dental Care, Kalyan — implants, braces vs aligners, root canal, whitening costs and everyday oral health tips.",
    canonical: "/blogs",
  },
  "/book-appointment": {
    title: "Book a Dental Appointment in Kalyan | Mutha's Advanced Dental Care",
    description:
      "Book your dental appointment at Mutha's Advanced Dental Care, Kalyan. Choose your treatment, pick a slot, or message us on WhatsApp for same-day help.",
    canonical: "/book-appointment",
  },
  "/contact": {
    title: "Contact & Clinic Location | Dentist in Kalyan",
    description:
      "Call, WhatsApp or visit Mutha's Advanced Dental Care in Kalyan. Clinic address, directions from Kalyan station, timings and appointment enquiry form.",
    canonical: "/contact",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Mutha's Advanced Dental Care Kalyan",
    description:
      "How Mutha's Advanced Dental Care, Kalyan collects, uses and protects the personal and health information you share with our clinic.",
    canonical: "/privacy-policy",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | Mutha's Advanced Dental Care Kalyan",
    description:
      "Terms and conditions for using the Mutha's Advanced Dental Care website, appointment requests and online enquiry forms.",
    canonical: "/terms-conditions",
  },
  "/thank-you": {
    title: "Thank You | Mutha's Advanced Dental Care Kalyan",
    description: "Thanks for reaching out to Mutha's Advanced Dental Care, Kalyan. Our team will call you back shortly.",
    canonical: "/thank-you",
    noindex: true,
  },
};

export const getPageSeo = (pathname: string): PageSeo | undefined => {
  if (pathname.startsWith("/admin")) {
    return {
      title: "Admin | Mutha's Advanced Dental Care",
      description: "Internal clinic administration area.",
      canonical: pathname,
      noindex: true,
    };
  }
  const key = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const seo = PAGE_SEO[key];
  if (!seo) return undefined;
  const label = BREADCRUMB_LABELS[key];
  if (!label || seo.noindex) return seo;
  const existing = seo.jsonLd ? (Array.isArray(seo.jsonLd) ? seo.jsonLd : [seo.jsonLd]) : [];
  return {
    ...seo,
    jsonLd: [...existing, breadcrumbJsonLd([{ name: label, path: seo.canonical }])],
  };
};
